class StickyHeader extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        this.header = document.querySelector('.section-header');
        // Select the announcement bar (utility bar) to sync sticky behavior
        this.announcementBar = document.querySelector('.announcement-bar-section') || document.querySelector('.utility-bar');

        this.headerIsAlwaysSticky =
            this.getAttribute('data-sticky-type') === 'always' ||
            this.getAttribute('data-sticky-type') === 'reduce-logo-size';
        this.headerBounds = {};

        this.setHeaderHeight();

        window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));
        
        // Override global.js zero-height calculations after it fires
        window.addEventListener('resize', () => {
            setTimeout(this.setHeaderHeight.bind(this), 50);
        });
        
        document.addEventListener('click', (e) => {
            const summaryOrButton = e.target.closest('summary') || e.target.closest('details') || e.target.closest('button');
            if (summaryOrButton) {
                setTimeout(this.setHeaderHeight.bind(this), 50);
                setTimeout(this.setHeaderHeight.bind(this), 250); // Catch after drawer animation
            }
        });

        if (this.headerIsAlwaysSticky) {
            this.header.classList.add('shopify-section-header-sticky');
            if (this.announcementBar) this.announcementBar.classList.add('shopify-section-header-sticky');
        }

        this.currentScrollTop = 0;
        this.preventReveal = false;
        this.predictiveSearch = this.querySelector('predictive-search');

        this.onScrollHandler = this.onScroll.bind(this);
        this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

        this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
        window.addEventListener('scroll', this.onScrollHandler, false);

        this.createObserver();

        // Initial position check for transparent header
        this.updateTransparentHeaderPosition();
        this.updateHeaderTransparency(window.pageYOffset || document.documentElement.scrollTop);
    }

    setHeaderHeight() {
        const visualHeader = this.querySelector('header') || this;
        document.documentElement.style.setProperty('--header-height', `${visualHeader.offsetHeight}px`);
        document.documentElement.style.setProperty('--header-bottom-position', `${parseInt(visualHeader.getBoundingClientRect().bottom)}px`);
        if (this.announcementBar) {
            document.documentElement.style.setProperty('--announcement-height-height', `${this.announcementBar.offsetHeight}px`);
        }
    }

    disconnectedCallback() {
        this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
        window.removeEventListener('scroll', this.onScrollHandler);
    }

    createObserver() {
        let observer = new IntersectionObserver((entries, observer) => {
            this.headerBounds = entries[0].intersectionRect;
            observer.disconnect();
        });

        observer.observe(this.header);
    }

    onScroll() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

        // Update transparent header position
        this.updateTransparentHeaderPosition();
        this.updateHeaderTransparency(scrollTop);

        const headerHeight = this.header.offsetHeight;

        if (this.announcementBar) {
            if (scrollTop > headerHeight) {
                this.announcementBar.classList.add('scrolled-20px');
            } else {
                this.announcementBar.classList.remove('scrolled-20px');
            }
        }

        if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

        if (scrollTop > this.currentScrollTop && scrollTop > headerHeight) {
            this.header.classList.add('scrolled-past-header');
            if (this.preventHide) return;
            requestAnimationFrame(this.hide.bind(this));
        } else if (scrollTop < this.currentScrollTop && scrollTop > headerHeight) {
            this.header.classList.add('scrolled-past-header');
            if (!this.preventReveal) {
                requestAnimationFrame(this.reveal.bind(this));
            } else {
                window.clearTimeout(this.isScrolling);
                this.isScrolling = setTimeout(() => {
                    this.preventReveal = false;
                }, 66);
                requestAnimationFrame(this.hide.bind(this));
            }
        } else if (scrollTop <= headerHeight) {
            this.header.classList.remove('scrolled-past-header');
            requestAnimationFrame(this.reset.bind(this));
        }
        this.currentScrollTop = scrollTop;
    }

    updateTransparentHeaderPosition() {
        const wrapper = this.header.querySelector('.header-wrapper--transparent');
        if (!wrapper) return;

        const announcementBar = document.querySelector('.utility-bar__grid--custom') || this.announcementBar;
        let topPosition = 0;

        if (announcementBar) {
            topPosition = announcementBar.offsetHeight;
        }

        document.body.style.setProperty('--header-top-position', `${topPosition}px`);
    }

    updateHeaderTransparency(scrollTop) {
        const transparentWrapper = this.header.querySelector('.header-wrapper--transparent');
        if (!transparentWrapper) return;

        const threshold = 10; // Scroll threshold to switch classes

        if (scrollTop <= threshold) {
            document.body.classList.add('header-transparent');
            document.body.classList.remove('header-colored');
        } else {
            document.body.classList.add('header-colored');
            document.body.classList.remove('header-transparent');
        }
    }

    hide() {
        if (this.headerIsAlwaysSticky) return;
        this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
        if (this.announcementBar) {
            this.announcementBar.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
            // Ensure transition is enabled
            this.announcementBar.classList.add('animate');
        }
        this.closeMenuDisclosure();
        this.closeSearchModal();
    }

    reveal() {
        if (this.headerIsAlwaysSticky) return;
        this.header.classList.add('shopify-section-header-sticky', 'animate');
        this.header.classList.remove('shopify-section-header-hidden');
        if (this.announcementBar) {
            this.announcementBar.classList.add('shopify-section-header-sticky', 'animate');
            this.announcementBar.classList.remove('shopify-section-header-hidden');
        }
    }

    reset() {
        if (this.headerIsAlwaysSticky) return;
        this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
        if (this.announcementBar) {
            this.announcementBar.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
        }
    }

    closeMenuDisclosure() {
        this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
        this.disclosures.forEach((disclosure) => disclosure.close());

        const headerDrawer = this.header.querySelector('header-drawer');
        if (headerDrawer && headerDrawer.mainDetailsToggle.hasAttribute('open')) {
            headerDrawer.closeMenuDrawer(new Event('scroll'), headerDrawer.mainDetailsToggle.querySelector('summary'));
        }
    }

    closeSearchModal() {
        this.searchModal = this.searchModal || this.header.querySelector('details-modal');
        this.searchModal.close(false);
    }
}

customElements.define('sticky-header', StickyHeader);

